<template>
  <div v-if="headline || formattedText || formattedList.length">
    <Headline
      v-if="headline && (formattedText || formattedList.length)"
      class="mb-6"
      tag="span"
      size="xl"
    >
      {{ headline }}
    </Headline>
    <div
      v-if="['short', 'long'].includes(type) && formattedText"
      v-dompurify-html="formattedText"
      class="prose max-w-full"
    />
    <ul
      v-else-if="type === 'list' && formattedList.length"
      :class="`with-checkmarks list-inside space-y-${listSpacing}`"
    >
      <li
        v-for="entry in formattedList"
        :key="entry"
        v-dompurify-html="entry"
      ></li>
    </ul>
  </div>
</template>

<script setup lang="ts">
import {
  type AdvancedAttribute,
  type AttributeGroup,
  flattenFieldSet,
} from '@scayle/storefront-nuxt'

const props = defineProps({
  getAttribute: {
    type: Function as PropType<
      (
        key: string,
        full?: boolean,
      ) => string | AttributeGroup | AdvancedAttribute | null | undefined
    >,
    required: true,
  },
  headline: {
    type: String as PropType<string | undefined | null>,
    default: '',
  },
  type: {
    type: String as PropType<'long' | 'short' | 'list'>,
    required: true,
  },
  listSpacing: {
    type: Number as PropType<2 | 4>,
    default: 4,
  },
})

const rawAttribute = ref()
const { type } = toRefs(props)

const scope = effectScope()
onScopeDispose(() => scope.stop())

scope.run(() => {
  if (import.meta.server) {
    return
  }
  watch(type, () => {
    fetchAttribute()
  })
})

const fetchAttribute = () => {
  if (props.type === 'list') {
    rawAttribute.value = props.getAttribute(
      'productAdvantagesDescription',
      true,
    )
  } else if (props.type === 'long') {
    rawAttribute.value = props.getAttribute('longDescriptionFormatted')
  } else if (props.type === 'short') {
    rawAttribute.value = props.getAttribute('shortDescriptionFormatted')
  }
}
fetchAttribute()

const formattedText = computed(() => {
  if (!rawAttribute.value || typeof rawAttribute.value !== 'string') {
    return undefined
  }
  return formatAllowedHtml(parseMarkdownAttribute(rawAttribute.value))
})

const isListValidItem = (input: any): input is { value: string } =>
  'value' in input && typeof input.value === 'string'

const formattedList = computed(() => {
  const res =
    (rawAttribute.value?.values && rawAttribute.value?.values[0]?.fieldSet) ||
    []

  const listItems = flattenFieldSet(res)

  // Fixes SCFIM-1436 because Akeneo returns just a single advantage with multiple paragraphs instead of multiple advantages
  return listItems.flatMap((entry) => {
    if (isListValidItem(entry)) {
      // sometimes we get <ul> with <li> inside, sometimes we get a list of <p> or <div>
      // always convert it to a list of <div> and then split into separate items
      let cleanEntry = entry.value
      if (entry.value.includes('<ul>')) {
        cleanEntry = cleanEntry.replaceAll('<ul>', '')
        cleanEntry = cleanEntry.replaceAll('</ul>', '')
        cleanEntry = cleanEntry.replaceAll('<li>', '<div>')
        cleanEntry = cleanEntry.replaceAll('</li>', '</div>')
      } else {
        cleanEntry = cleanEntry.replaceAll('<p>', '<div>')
        cleanEntry = cleanEntry.replaceAll('</p>', '</div>')
      }

      const regex = /<div[^>]*>/

      return cleanEntry
        .split(regex)
        .map((item) => item.replaceAll('</div>', ''))
        .filter(Boolean)
    }
    return []
  })
})
</script>
